import { useState, useEffect } from 'react'
import { useUnitTranslation } from '@/shared/contexts/TranslationContext'
import { getContexts, listGroupsByContext, Context, AssetGroup } from './contextsService'
import { ContextIcon } from '@/shared/components/ui'

interface ContextGroupSelectorProps {
  selectedContextKey?: string | null
  selectedGroupIds?: string[]
  onContextChange?: (contextKey: string) => void
  onGroupsChange?: (groupIds: string[]) => void
  hideContextSelector?: boolean
  className?: string
}

export function ContextGroupSelector({
  selectedContextKey,
  selectedGroupIds = [],
  onContextChange,
  onGroupsChange,
  hideContextSelector = false,
  className = ''
}: ContextGroupSelectorProps) {
  const { tu } = useUnitTranslation()
  const [contexts, setContexts] = useState<Context[]>([])
  const [groups, setGroups] = useState<AssetGroup[]>([])
  const [loadingContexts, setLoadingContexts] = useState(true)
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [selectedContext, setSelectedContext] = useState<string | null>(selectedContextKey || null)
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set(selectedGroupIds))

  // Load contexts on mount
  useEffect(() => {
    loadContexts()
  }, [])

  // Load groups when context changes
  useEffect(() => {
    if (selectedContext) {
      loadGroups(selectedContext)
    } else {
      setGroups([])
    }
  }, [selectedContext])

  // Update local state when props change
  useEffect(() => {
    setSelectedContext(selectedContextKey || null)
  }, [selectedContextKey])

  useEffect(() => {
    setSelectedGroups(new Set(selectedGroupIds))
  }, [selectedGroupIds])

  const loadContexts = async () => {
    try {
      setLoadingContexts(true)
      const data = await getContexts()
      setContexts(data)
    } catch (error) {
      console.error('Error loading contexts:', error)
    } finally {
      setLoadingContexts(false)
    }
  }

  const loadGroups = async (contextKey: string) => {
    try {
      setLoadingGroups(true)
      const data = await listGroupsByContext(contextKey)
      // Filter only active groups
      const filteredGroups = data.filter(g => (g as any).is_active !== false)
      setGroups(filteredGroups)
    } catch (error) {
      console.error('Error loading groups:', error)
      setGroups([])
    } finally {
      setLoadingGroups(false)
    }
  }

  const handleContextSelect = (contextKey: string) => {
    setSelectedContext(contextKey)
    // Clear selected groups when context changes
    setSelectedGroups(new Set())
    if (onContextChange) {
      onContextChange(contextKey)
    }
    if (onGroupsChange) {
      onGroupsChange([])
    }
  }

  const handleGroupToggle = (groupId: string) => {
    const newSelected = new Set(selectedGroups)
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId)
    } else {
      newSelected.add(groupId)
    }
    setSelectedGroups(newSelected)
    if (onGroupsChange) {
      onGroupsChange(Array.from(newSelected))
    }
  }

  const getContextName = (contextKey: string): string => {
    const key = `contexts.${contextKey}`
    return tu(key)
  }

  if (loadingContexts) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Context Selection */}
      {!hideContextSelector && (
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            {tu('contexts.selectContext')}
          </label>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {tu('contexts.selectContextDescription')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {contexts.map((context) => (
              <button
                key={context.id}
                type="button"
                onClick={() => handleContextSelect(context.key)}
                className={`
                  flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all
                  ${
                    selectedContext === context.key
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700'
                  }
                `}
              >
                <ContextIcon 
                  context={context.key as any} 
                  className={`w-8 h-8 mb-2 ${
                    selectedContext === context.key
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-slate-400 dark:text-slate-500'
                  }`}
                />
                <span className={`text-sm font-medium ${
                  selectedContext === context.key
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-slate-700 dark:text-slate-300'
                }`}>
                  {getContextName(context.key)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Groups Selection */}
      {selectedContext && (
        <div className={hideContextSelector ? '' : 'border-t border-slate-200 dark:border-slate-700 pt-6'}>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            {tu('contexts.selectGroups')}
          </label>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {tu('contexts.selectGroupsDescription')}
          </p>

          {loadingGroups ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              {tu('contexts.noGroupsAvailable')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {groups.map((group) => (
                <label
                  key={group.id}
                  className={`
                    flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${
                      selectedGroups.has(group.id)
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={selectedGroups.has(group.id)}
                    onChange={() => handleGroupToggle(group.id)}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-slate-300 dark:border-slate-600 rounded"
                  />

                  <span className={`ml-3 text-sm font-medium ${
                    selectedGroups.has(group.id)
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-slate-700 dark:text-slate-300'
                  }`}>
                    {group.name}
                  </span>

                  {group.is_system && (
                    <span className="ml-auto text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                      Sistema
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
